import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';

export interface FloatingElementConfig {
  id: string;
  priority: number;
  width: number;
  height: number;
  preferredPosition: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right';
  };
  offset: {
    x: number;
    y: number;
  };
  isVisible: boolean;
}

export interface CalculatedPosition {
  bottom?: number;
  top?: number;
  left?: number;
  right?: number;
  zIndex: number;
}

interface FloatingElementsContextType {
  registerElement: (config: FloatingElementConfig) => void;
  unregisterElement: (id: string) => void;
  updateVisibility: (id: string, isVisible: boolean) => void;
  getPosition: (id: string) => CalculatedPosition;
  viewportSize: { width: number; height: number };
}

const FloatingElementsContext = createContext<FloatingElementsContextType | null>(null);

const MOBILE_NAV_HEIGHT = 80;
const SAFE_MARGIN = 16;
const BASE_Z_INDEX = 40;

function configEquals(a: FloatingElementConfig, b: FloatingElementConfig): boolean {
  return (
    a.id === b.id &&
    a.priority === b.priority &&
    a.width === b.width &&
    a.height === b.height &&
    a.preferredPosition.vertical === b.preferredPosition.vertical &&
    a.preferredPosition.horizontal === b.preferredPosition.horizontal &&
    a.offset.x === b.offset.x &&
    a.offset.y === b.offset.y &&
    a.isVisible === b.isVisible
  );
}

export const FloatingElementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [elements, setElements] = useState<Map<string, FloatingElementConfig>>(new Map());
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const positionsRef = useRef<Map<string, CalculatedPosition>>(new Map());
  const [positionsVersion, setPositionsVersion] = useState(0);

  // Track viewport size with debounce
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setViewportSize({ width: window.innerWidth, height: window.innerHeight });
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Calculate positions whenever elements or viewport changes
  useEffect(() => {
    const newPositions = new Map<string, CalculatedPosition>();
    const visibleElements = Array.from(elements.values())
      .filter(el => el.isVisible)
      .sort((a, b) => b.priority - a.priority);

    const isMobile = viewportSize.width < 640;
    const occupiedRegions: Array<{ x: number; y: number; width: number; height: number; id: string }> = [];

    visibleElements.forEach((element, index) => {
      const position: CalculatedPosition = {
        zIndex: BASE_Z_INDEX + element.priority + index,
      };

      let x = element.preferredPosition.horizontal === 'right'
        ? viewportSize.width - element.width - element.offset.x
        : element.offset.x;

      let y = element.preferredPosition.vertical === 'bottom'
        ? viewportSize.height - element.height - element.offset.y - (isMobile ? MOBILE_NAV_HEIGHT : 0)
        : element.offset.y;

      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        const collision = occupiedRegions.find(region => {
          const overlapX = x < region.x + region.width + SAFE_MARGIN &&
                          x + element.width + SAFE_MARGIN > region.x;
          const overlapY = y < region.y + region.height + SAFE_MARGIN &&
                          y + element.height + SAFE_MARGIN > region.y;
          return overlapX && overlapY;
        });

        if (!collision) break;

        if (element.preferredPosition.vertical === 'bottom') {
          y = collision.y - element.height - SAFE_MARGIN;
        } else {
          y = collision.y + collision.height + SAFE_MARGIN;
        }

        if (y < SAFE_MARGIN || y + element.height > viewportSize.height - SAFE_MARGIN) {
          y = element.preferredPosition.vertical === 'bottom'
            ? viewportSize.height - element.height - element.offset.y - (isMobile ? MOBILE_NAV_HEIGHT : 0)
            : element.offset.y;

          if (element.preferredPosition.horizontal === 'right') {
            x = collision.x - element.width - SAFE_MARGIN;
          } else {
            x = collision.x + collision.width + SAFE_MARGIN;
          }
        }

        attempts++;
      }

      x = Math.max(SAFE_MARGIN, Math.min(x, viewportSize.width - element.width - SAFE_MARGIN));
      y = Math.max(SAFE_MARGIN, Math.min(y, viewportSize.height - element.height - SAFE_MARGIN - (isMobile ? MOBILE_NAV_HEIGHT : 0)));

      if (element.preferredPosition.vertical === 'bottom') {
        position.bottom = viewportSize.height - y - element.height;
      } else {
        position.top = y;
      }

      if (element.preferredPosition.horizontal === 'right') {
        position.right = viewportSize.width - x - element.width;
      } else {
        position.left = x;
      }

      newPositions.set(element.id, position);
      occupiedRegions.push({ x, y, width: element.width, height: element.height, id: element.id });
    });

    positionsRef.current = newPositions;
    setPositionsVersion(v => v + 1);
  }, [elements, viewportSize]);

  const registerElement = useCallback((config: FloatingElementConfig) => {
    setElements(prev => {
      const existing = prev.get(config.id);
      if (existing && configEquals(existing, config)) {
        return prev; // No change
      }
      const next = new Map(prev);
      next.set(config.id, config);
      return next;
    });
  }, []);

  const unregisterElement = useCallback((id: string) => {
    setElements(prev => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const updateVisibility = useCallback((id: string, isVisible: boolean) => {
    setElements(prev => {
      const element = prev.get(id);
      if (!element || element.isVisible === isVisible) return prev;
      const next = new Map(prev);
      next.set(id, { ...element, isVisible });
      return next;
    });
  }, []);

  const getPosition = useCallback((id: string): CalculatedPosition => {
    return positionsRef.current.get(id) || { zIndex: BASE_Z_INDEX };
  }, [positionsVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({
    registerElement,
    unregisterElement,
    updateVisibility,
    getPosition,
    viewportSize,
  }), [registerElement, unregisterElement, updateVisibility, getPosition, viewportSize]);

  return (
    <FloatingElementsContext.Provider value={value}>
      {children}
    </FloatingElementsContext.Provider>
  );
};

export const useFloatingElements = () => {
  const context = useContext(FloatingElementsContext);
  if (!context) {
    return {
      registerElement: () => {},
      unregisterElement: () => {},
      updateVisibility: () => {},
      getPosition: () => ({ zIndex: BASE_Z_INDEX }),
      viewportSize: { width: window.innerWidth, height: window.innerHeight },
    };
  }
  return context;
};
